/**
 * In-app documentation of the fill contract, surfaced from the Templates tab.
 * The copy is the guarantee the fill engine actually enforces — value
 * substitution only, what round-trips untouched, the active-content decision,
 * kept in step with the "What Polly fills" section of the architecture docs.
 */
import {
    Check,
    HelpCircle,
    FileSpreadsheet,
    Layers,
    ShieldAlert,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import {
    Popover,
    PopoverContent,
    PopoverHeader,
    PopoverTitle,
    PopoverTrigger,
} from "@/components/ui/popover";

const SECTIONS = [
    {
        Icon: Check,
        title: "Value substitution only",
        body: "Polly writes values into a copy of your file — a blank answer leaves the cell blank. It never recomputes, restructures, or regenerates the document.",
    },
    {
        Icon: Layers,
        title: "Preserved, not filled",
        body: "Charts, pivot tables, formulas, embedded images and logos, and conditional formatting round-trip into the filled file unchanged. Polly fills values around them.",
    },
    {
        Icon: ShieldAlert,
        title: "Active content",
        body: "PDF scripts and spreadsheet external references pass through unmodified. Macro-enabled workbooks (.xlsm / vbaProject.bin) are rejected at upload.",
    },
    {
        Icon: FileSpreadsheet,
        title: "Spreadsheets stay in Excel",
        body: "Spreadsheet templates do not have a browser preview. Downloaded .xlsx outputs remain faithful copies of the original workbook.",
    },
];

export function TemplatesHelpPopover() {
    return (
        <Popover>
            <PopoverTrigger
                render={
                    <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label="What Polly fills"
                        className="text-muted-foreground hover:text-foreground h-3.5 w-3.5 shrink-0"
                    />
                }
            >
                <HelpCircle className="size-4" />
            </PopoverTrigger>
            <PopoverContent side="bottom" align="end" className="w-85 p-4">
                <PopoverHeader className="mb-3">
                    <PopoverTitle className="text-sm font-semibold">
                        What Polly fills
                    </PopoverTitle>
                </PopoverHeader>
                <ul className="flex flex-col gap-3">
                    {SECTIONS.map(({ Icon, title, body }) => (
                        <li key={title} className="flex gap-2.5">
                            <span className="bg-muted text-muted-foreground mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-md">
                                <Icon className="size-3.5" aria-hidden />
                            </span>
                            <div className="min-w-0">
                                <p className="text-sm font-medium">{title}</p>
                                <p className="text-muted-foreground mt-0.5 text-xs">
                                    {body}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            </PopoverContent>
        </Popover>
    );
}
